// Debug state projection used by Phase 4+ browser tests. Extracted
// from main.ts to keep that file under the §10 cap and to give the
// __nexusDebug surface a dedicated home that's easy to grep.
//
// All reads go through getters provided at construction — keeps this
// module ignorant of clientState / marketUI internals and keeps the
// production check (gated on DEV or ?test in main.ts) in one place.
// See DECISIONS 2026-04-18 "Phase 4 Step 2 — __nexusDebug gated on
// DEV or ?test".
//
// Phase 5 Step 21 — semantic action hooks for combat / party /
// expedition / repair. PixiJS v8 in headless Chromium drops native
// PointerEvents on the canvas before they reach hit-tested children,
// so the browser harness can't drive these widgets via mouse clicks.
// The hooks call into the same handlers the user-facing pointer
// events would, named by what the user is doing rather than where
// they're clicking. Lives behind the __nexusDebug gate (DEV or ?test
// only); production never reaches this code.

import type { Application } from "pixi.js";
import {
  buildEnterExpeditionMessage,
  buildAcceptExpeditionReadyMessage,
  buildAcceptPartyInviteMessage,
  buildDeclineExpeditionReadyMessage,
  buildDeclinePartyInviteMessage,
  buildExitExpeditionMessage,
  buildInviteToPartyMessage,
  buildLeavePartyMessage,
} from "../../shared/expedition-messages.ts";
import { buildUseAbilityMessage } from "../../shared/combat-messages.ts";
import { buildMoveMessage, buildRepairItemMessage, type ClientMessage } from "../../shared/messages.ts";
import {
  buildCancelCraftMessage,
  buildClaimCompletedJobsMessage,
  buildQueueCraftMessage,
} from "../../shared/workshop-messages.ts";
import { setTarget, type CombatClientState } from "./combat-state.ts";
import { DISMISSED_INVITES_STORAGE_KEY, type ExpeditionClientState } from "./expedition-state.ts";
import type { PartyClientState } from "./party-state.ts";
import type { InventorySlot, PlayerState, ResourceNode, SkillEntry, WorkshopState } from "./types.ts";
import type { EquippedItemReadout } from "../../shared/equip-messages.ts";
import type { ClientMarketState, MarketTab } from "./market-ui.ts";
import type { MarketUI } from "./market-ui.ts";
import type { WorkbenchUI } from "./workbench-ui.ts";
import type { PartyUI } from "./party-ui.ts";
import { LIST_FORM_FEE_NOTICE, computeListingFee } from "./market-list-form.ts";
import { CANCEL_LISTING_HINT } from "./market-listings-tab.ts";

export type NexusDebugGetters = {
  self: () => PlayerState | null;
  players: () => Iterable<PlayerState>;
  inventory: () => readonly InventorySlot[];
  activeGather: () => { nodeId: string; startedAt: number; expectedCompleteAt: number } | null;
  nodes: () => Iterable<ResourceNode>;
  workshop: () => WorkshopState;
  skills: () => readonly SkillEntry[];
  credits: () => number;
  market: () => ClientMarketState;
  marketUI: MarketUI;
  // Phase 5.1 Step 2 — exposes workbench panel open-state so the
  // browser harness can assert real keypress B → panel opens, then
  // craftQueued envelope arrives → panel jobs reflect new state.
  workbenchUI: WorkbenchUI;
  // Phase 5.1 Step 3 — exposes party panel open-state for the
  // party-panel-hints-solo user-flow scenario.
  partyUI: PartyUI;
  app: Application;
  // Phase 5 Step 21 additions.
  currentZoneId: () => string | null;
  combat: () => CombatClientState;
  party: () => PartyClientState;
  expedition: () => ExpeditionClientState;
  // Phase 7 Block C escape — equippedItem not projected; test assertions need it.
  equippedItem: () => EquippedItemReadout | null;
};

// Action surfaces for the test harness. Action method names avoid
// `list` / `get` etc. so the floating-promise contract scanner doesn't
// mis-match the type signatures as promise-returning call sites.
type MarketActions = {
  setTab(tab: MarketTab): void;
  selectInventorySlot(slotIndex: number): { ok: boolean; reason?: string };
  setListPrice(price: number): void;
  submitListing(): { ok: boolean; reason?: string };
  buyListing(listingId: string): void;
  cancelListing(listingId: string): void;
  repairItem(rowid: number): void;
};

type CombatActions = {
  setTarget(entityId: string | null): void;
  useAbility(abilityId: string, targetId: string): void;
  move(x: number, y: number): void;
};

type PartyActions = {
  invite(targetPlayerId: string): void;
  accept(partyId: string): void;
  decline(partyId: string): void;
  leave(): void;
};

type ExpeditionActions = {
  triggerEnter(archetype: string): void;
  acceptReady(expeditionId: string): void;
  declineReady(expeditionId: string): void;
  exit(): void;
};

type NetworkActions = {
  reconnect(): void;
  updateZoneId(zoneId: string): void;
};

// Phase 5.1 Step 2 — workbench action hooks. PixiJS canvas hit-test on
// the Queue Craft / Cancel / Claim buttons is unreliable in headless
// Chromium (same DECISIONS 2026-04-18 rationale as the market actions).
// These hooks call the same builders the production workbench-action-bar
// pointertap handlers call, routed through the same network.send path.
type WorkbenchActions = {
  queueCraft(recipeId: string, batchCount: number): void;
  cancelCraft(jobId: string): void;
  // Phase 7 Block D — `jobId` optional. Omit for the historical claim-all,
  // pass to claim a single completed job per the per-row workbench UX.
  claimCompletedJobs(jobId?: string): void;
  // Phase 7 Block D — pointer-equivalent recipe selection for the
  // browser harness. PixiJS recipe-row pointer-taps are unreliable in
  // headless Chromium; this routes through the same setRecipe path the
  // production click handler uses.
  selectRecipe(recipeId: string): { ok: boolean; reason?: string };
};

export type NexusDebugActionDeps = {
  send: (msg: ClientMessage) => void;
  reconnect: () => void;
  updateZoneId: (zoneId: string) => void;
  combatState: CombatClientState;
  expeditionState: ExpeditionClientState;
  storage: { setItem(k: string, v: string): void };
};

function buildMarketActions(
  marketUI: MarketUI,
  getInventory: () => readonly InventorySlot[],
  send: (msg: ClientMessage) => void,
): MarketActions {
  return {
    setTab: (tab) => marketUI.setActiveTab(tab),
    selectInventorySlot: (slotIndex) => {
      const slot = getInventory().find((s) => s.slotIndex === slotIndex);
      if (!slot) return { ok: false, reason: "slot empty" };
      marketUI.listingsState.handleInventorySelect(slotIndex, slot);
      return { ok: true };
    },
    setListPrice: (price) => {
      const delta = price - marketUI.listingsState.listPrice;
      if (delta !== 0) marketUI.listingsState.handlePriceDelta(delta);
    },
    submitListing: () => {
      const sel = marketUI.listingsState.selectedSlot;
      if (!sel) return { ok: false, reason: "no slot selected" };
      marketUI.listingsState.handleList(sel.itemId, marketUI.listingsState.listPrice);
      return { ok: true };
    },
    buyListing: (id) => marketUI.listingsState.handleBuy(id),
    cancelListing: (id) => marketUI.listingsState.handleCancel(id),
    repairItem: (rowid) => send(buildRepairItemMessage(rowid)),
  };
}

function buildCombatActions(deps: NexusDebugActionDeps): CombatActions {
  return {
    setTarget: (entityId) => setTarget(deps.combatState, entityId),
    useAbility: (abilityId, targetId) => {
      // Cast: AbilityId is a string-literal union; the test harness
      // passes a string for ergonomics. Server validates the id.
      deps.send(buildUseAbilityMessage(abilityId as never, targetId));
    },
    move: (x, y) => deps.send(buildMoveMessage(x, y, Date.now())),
  };
}

function buildPartyActions(deps: NexusDebugActionDeps): PartyActions {
  return {
    invite: (targetPlayerId) => deps.send(buildInviteToPartyMessage(targetPlayerId)),
    accept: (partyId) => deps.send(buildAcceptPartyInviteMessage(partyId)),
    decline: (partyId) => deps.send(buildDeclinePartyInviteMessage(partyId)),
    leave: () => deps.send(buildLeavePartyMessage()),
  };
}

function buildExpeditionActions(deps: NexusDebugActionDeps): ExpeditionActions {
  return {
    triggerEnter: (archetype) => deps.send(buildEnterExpeditionMessage(archetype)),
    acceptReady: (expeditionId) => {
      // Mirrors expedition-ready-ui.ts onAccept: send + flag the
      // pending-accept so the resume reconnect lands on ExpeditionDO
      // when the server's expeditionEntered envelope arrives via
      // replay buffer.
      deps.send(buildAcceptExpeditionReadyMessage(expeditionId));
      deps.expeditionState.acceptedPending = expeditionId;
      deps.expeditionState.pendingReady = null;
    },
    declineReady: (expeditionId) => {
      deps.send(buildDeclineExpeditionReadyMessage(expeditionId));
      deps.expeditionState.dismissedInvites.add(expeditionId);
      deps.storage.setItem(
        DISMISSED_INVITES_STORAGE_KEY,
        JSON.stringify([...deps.expeditionState.dismissedInvites]),
      );
      deps.expeditionState.pendingReady = null;
    },
    exit: () => {
      deps.send(buildExitExpeditionMessage());
      deps.expeditionState.completed = null;
    },
  };
}

function buildNetworkActions(deps: NexusDebugActionDeps): NetworkActions {
  return {
    reconnect: () => deps.reconnect(),
    updateZoneId: (zoneId: string) => deps.updateZoneId(zoneId),
  };
}

function buildWorkbenchActions(
  deps: NexusDebugActionDeps,
  workbenchUI: WorkbenchUI,
): WorkbenchActions {
  return {
    queueCraft: (recipeId, batchCount) =>
      deps.send(buildQueueCraftMessage(recipeId, batchCount)),
    cancelCraft: (jobId) => deps.send(buildCancelCraftMessage(jobId)),
    claimCompletedJobs: (jobId) => deps.send(buildClaimCompletedJobsMessage(jobId)),
    selectRecipe: (recipeId) => workbenchUI.selectRecipeForTest(recipeId),
  };
}

export function buildNexusDebug(
  g: NexusDebugGetters,
  actionDeps: NexusDebugActionDeps,
): {
  state: () => NexusDebugState;
  market: MarketActions;
  combat: CombatActions;
  party: PartyActions;
  expedition: ExpeditionActions;
  network: NetworkActions;
  workbench: WorkbenchActions;
} {
  return {
    market: buildMarketActions(g.marketUI, g.inventory, actionDeps.send),
    combat: buildCombatActions(actionDeps),
    party: buildPartyActions(actionDeps),
    expedition: buildExpeditionActions(actionDeps),
    network: buildNetworkActions(actionDeps),
    workbench: buildWorkbenchActions(actionDeps, g.workbenchUI),
    state: () => {
      const self = g.self();
      const gather = g.activeGather();
      const combat = g.combat();
      const party = g.party();
      const expedition = g.expedition();
      const snapshot = {

        self: self ? structuredClone(self) : null,
        players: structuredClone(Array.from(g.players())),
        inventory: structuredClone(g.inventory()),
        activeGather: gather ? structuredClone(gather) : null,
        nodes: structuredClone(Array.from(g.nodes())),
        workshop: structuredClone(g.workshop()),
        skills: structuredClone(g.skills()),
        credits: g.credits(),
        market: structuredClone(g.market()),
        currentZoneId: g.currentZoneId(),
        combat: {
          targetId: combat.targetId,
          mobIds: Array.from(combat.mobHp.keys()),
          mobHp: Object.fromEntries(combat.mobHp),
          playerHp: Object.fromEntries(combat.playerHp),
          selfHp: { ...combat.selfHp },
          feedbackCount: combat.feedback.length,
        },
        party: {
          roster: party.roster
            ? {
                partyId: party.roster.partyId,
                leaderId: party.roster.leaderId,
                members: party.roster.members.map((m) => ({ memberId: m.memberId })),
              }
            : null,
          pendingInvites: party.pendingInvites.map((i) => ({
            partyId: i.partyId,
            leaderId: i.leaderId,
            leaderName: i.leaderName,
          })),
        },
        expedition: {
          pendingReady: expedition.pendingReady
            ? {
                expeditionId: expedition.pendingReady.expeditionId,
                archetype: expedition.pendingReady.archetype,
                leaderId: expedition.pendingReady.leaderId,
              }
            : null,
          completed: expedition.completed
            ? { expeditionId: expedition.completed.expeditionId, reason: expedition.completed.reason }
            : null,
          acceptedPending: expedition.acceptedPending,
          dismissedInvites: Array.from(expedition.dismissedInvites),
        },
        marketPanel: {
          open: g.marketUI.isOpen,
          activeTab: g.marketUI.activeTab,
          selectedSlotIndex: g.marketUI.selectedSlotIndex,
          listPrice: g.marketUI.listPrice,
          listPending: g.marketUI.listPending,
          pendingListingIds: [...g.marketUI.pendingListingIds],
          npcTab: {
            qtyByItem: { ...g.marketUI.npcTab.qtyByItem },
            buyPending: [...g.marketUI.npcTab.buyPendingItems],
            sellPending: [...g.marketUI.npcTab.sellPendingItems],
          },
          historyTab: {
            selectedItemId: g.marketUI.historyTab.currentItemId,
            selectedRangeMs: g.marketUI.historyTab.currentRangeMs,
          },
          repairTab: {
            pendingRowids: [...g.marketUI.repairTab.pendingRowids],
          },
          // Phase 7 Block D — listing-fee copy + computed fee. Surfaces
          // the strings the listings tab actually renders so browser
          // tests can assert without reading PixiJS Text content.
          listFormFeeNotice: LIST_FORM_FEE_NOTICE,
          listFormFee: computeListingFee(g.marketUI.listPrice),
          cancelListingHint: CANCEL_LISTING_HINT,
        },
        workbenchPanel: {
          open: g.workbenchUI.isOpen,
          // Phase 7 Block D — materials gate + selected-recipe + listing
          // copy assertions. Browser tests can't read PixiJS Text content,
          // so the panel's current assessment is exposed as state. Null
          // when no recipe is selected. perInput preserves recipe order.
          materials: g.workbenchUI.currentMaterials(),
          selectedRecipeId: g.workbenchUI.selectedRecipeId,
        },
        partyPanel: {
          open: g.partyUI.isOpen,
        },
        equippedItem: ((_eq) => _eq !== null ? structuredClone(_eq) : null)(g.equippedItem()),
      };
      return snapshot as unknown as NexusDebugState;
    },
  };
}
